import { useCallback, useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { NAVIGATION_KEYS } from 'src/interfaces/enums';
import useUI from 'src/hooks/useUI';
import useClickOutside, { ClickOutsideEvent } from 'src/hooks/useClickOutside';
import useMenuRoute from 'src/themes/shared/containers/MenuRouteContainer/hooks/useMenuRoute';
import MenuRoute from 'src/themes/shared/containers/MenuRouteContainer/components/MenuRoute';
import { withMenuRouteContextProvider } from 'src/themes/shared/containers/MenuRouteContainer/context/MenuRouteContext';
import SwitchMenu from 'src/themes/shared/containers/MenuRouteContainer/containers/SwitchMenu';
import { detectMobile } from 'src/utils/browserUtils';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { MENU_ROUTES } from './routes';

import SubtitleControlContainer from './containers/SubtitleControlContainer';
import SubtitleStyleControlContainer from './containers/SubtitleStyleControlContainer';
import HomeSubtitleControlContainer from './containers/HomeSubtitleControlContainer';
import SubtitleColorsControl from './containers/SubtitleColorsControl';
import SubtitleBackgroundControl from './containers/SubtitleBackgroundControl';
import SubtitleStrokeControl from './containers/SubtitleStrokeControl';
import SubtitleOpacityControl from './containers/SubtitleOpacityControl';
import SubtitleSizeControl from './containers/SubtitleSizeControl';

const DELAY_TO_HIDE_SETTINGS = 100;

const SubtitleSettingsOverlay = () => {
  const { t } = useTranslation('common');
  const { goBack } = useMenuRoute();
  const isMobile = detectMobile();

  const { hideSubtitleSettings } = useUI();
  const [isPreparedToHide, setIsPreparedToHide] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: ClickOutsideEvent) => {
    event.stopPropagation();

    setIsPreparedToHide(true);
  }, []);

  useClickOutside(containerRef?.current, handleClickOutside);

  useEffect(() => {
    if (!isPreparedToHide) {
      return;
    }
    const timeout = setTimeout(hideSubtitleSettings, DELAY_TO_HIDE_SETTINGS);

    return () => {
      clearTimeout(timeout);
    };
  }, [isPreparedToHide, hideSubtitleSettings]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const { key } = event;

      switch (key) {
        case NAVIGATION_KEYS.ESCAPE:
          hideSubtitleSettings();
          break;
        case NAVIGATION_KEYS.ARROW_LEFT:
          goBack();
          break;
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [hideSubtitleSettings, goBack]);

  return (
    <SwitchMenu
      ref={containerRef}
      className={classNames(styles.container, { [styles.isMobile]: isMobile })}
      onClose={hideSubtitleSettings}
    >
      <MenuRoute title={t('subtitles.title')} path={MENU_ROUTES.HOME}>
        <HomeSubtitleControlContainer />
      </MenuRoute>
      <MenuRoute title={t('subtitles.languages')} path={MENU_ROUTES.SUBTITLES} showBackButton>
        <SubtitleControlContainer />
      </MenuRoute>
      <MenuRoute title={t('subtitles.styles.title')} path={MENU_ROUTES.STYLES} showBackButton>
        <SubtitleStyleControlContainer />
      </MenuRoute>
      <MenuRoute title={t('subtitles.styles.options.color')} path={MENU_ROUTES.STYLES_COLOR} showBackButton>
        <SubtitleColorsControl />
      </MenuRoute>
      <MenuRoute
        title={t('subtitles.styles.options.background')}
        path={MENU_ROUTES.STYLES_BACKGROUND_COLOR}
        showBackButton
      >
        <SubtitleBackgroundControl />
      </MenuRoute>
      <MenuRoute title={t('subtitles.styles.options.stroke')} path={MENU_ROUTES.STYLES_BORDER} showBackButton>
        <SubtitleStrokeControl />
      </MenuRoute>
      <MenuRoute
        title={t('subtitles.styles.options.opacity')}
        path={MENU_ROUTES.STYLES_BACKGROUND_OPACITY}
        showBackButton
      >
        <SubtitleOpacityControl />
      </MenuRoute>
      <MenuRoute title={t('subtitles.styles.options.font_size')} path={MENU_ROUTES.STYLES_FONT_SIZE} showBackButton>
        <SubtitleSizeControl />
      </MenuRoute>
    </SwitchMenu>
  );
};

export default withMenuRouteContextProvider(SubtitleSettingsOverlay);
